import React from "react";
import type { ApolloClient } from "@apollo/client";
import { toast } from "react-toastify";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { Card, Col, Container, Row } from "react-bootstrap";
import { each, get, isEmpty, size, some } from "lodash";
import queryString from "query-string";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import {
  activeAccountType,
  activeIdIsProcessingRollover,
  activeRolloverIsMixed,
  getIraAccountIdSelector,
  hasPaychexEmployeeWorkerRecords,
  isIndividualAccountOverview,
  isProcessingRolloverSelector,
  paycheckSplitContributionSelector,
  totalAccountBalance,
  userHasPaychexPerksLink,
  userHasPaychexRollover,
  userIs401kRollIn,
  userIsLockedSelector,
} from "store/selectors/user";
import { getAllDocuments } from "actions/documentActions";
import { getAllAccounts } from "actions/accountActions";
import { getTransactions } from "actions/transactionActions";
import { bankSelector, isBankVerifiedSelector } from "store/selectors/bank";
import { contributionAbilitySelector } from "store/selectors/contribution";
import {
  getEmployerLinkRequests,
  getLinkedEmployers,
  getPaycheckSplitContribution,
  getPaychexWorkerRecords,
} from "actions/userActions";
import {
  accountConstants,
  bankConstants,
  contributionConstants,
  transactionConstants,
  userConstants,
} from "actions/types";
import {
  getContributionAbility,
  getUserRecurringContributions,
} from "actions/contributionActions";
import { rolloverStates } from "statics/states";
import {
  auth0AccountSessionSelector,
  hasBothPreTaxAndRothFundsSelector,
  hasPreTaxFundsSelector,
  hasRothFundsSelector,
} from "store/selectors/auth0";
import { FiAlertTriangle } from "react-icons/fi";
import { BsCashStack } from "react-icons/bs";

import ActionCard from "components/ActionCard";
import Alert from "components/Alert";
import moment from "moment";
import PerformanceTable from "./PerformanceTable";
import BalanceKpi from "./BalanceKpi";
import PriorYearContributionCard from "./PriorYearContributionCard";
import PendingClosure from "pages/PendingClosure";
import SupportEmailLink from "components/SupportEmailLink";
import IconSpinner from "components/IconSpinner";
import IndividualOverview from "./IndividualOverview";
import IndividualRolloverCard from "./IndividualRolloverCard";
import IndividualRolloverFormCard from "./IndividualRolloverFormCard";
import ReAuthPlaidLink from "components/ReAuthPlaidLink";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import IconBanner from "components/IconBanner";
import PaychexDashboard from "./PaychexDashboard";
import PaychexRolloverStatus from "./PaychexRolloverStatus";
import PaychexSecondRolloverCard from "./PaychexSecondRolloverCard";
import PaychexFirstRolloverCard from "./PaychexFirstRolloverCard";
import IconMessageCard from "components/IconMessageCard";
import ContributionHistory from "pages/dashboards/adminDashboard/users/ContributionHistory";
import type { RootState } from "types/state";

import "./IndividualDashboard.scss";

const loadingSelector = createLoadingSelector([
  accountConstants.GET_ACCOUNTS,
  userConstants.USER_EMPLOYER_LINK_REQUESTS,
  transactionConstants.GET_TRANSACTIONS,
  contributionConstants.GET_RECURRING_CONTRIBUTIONS,
]);

const errorSelector = createErrorSelector([
  bankConstants.OAUTH_PLAID_LINK_TOKEN,
]);

// Only fetched for Perks holders (see componentDidMount). The dashboard picks
// between "your contribution is set up" and a start-contributing CTA, so it has
// to wait for both.
const perksContributionLoadingSelector = createLoadingSelector([
  userConstants.PAYCHEX_WORKER_RECORDS,
  userConstants.GET_PAYCHECK_SPLIT_CONTRIBUTION,
]);

const mapStateToProps = (state: RootState) => {
  const { profile: userInfo } = state.user;
  const accounts = state.accounts.iraAccounts;
  const iraAccountId = getIraAccountIdSelector(state);

  const isRolloverIsMixed = activeRolloverIsMixed(state);

  const activeRolloverAccount = some(accounts, {
    id: iraAccountId,
    isRollover: true,
    isProcessingRollover: false,
    state: "AccountActive",
  });

  const rolloverAccountIsActive = accounts.some(
    (account) =>
      account.isRollover &&
      !account.isProcessingRollover &&
      account.state === rolloverStates.AccountActive
  );

  const bank = bankSelector(state);
  const individualReAuthRequired = !!get(bank, "loginRequired");

  const hasFundedAccount = totalAccountBalance(state) > 0;

  return {
    hasFundedAccount,
    rolloverAccountIsActive,
    isRolloverIsMixed,
    activeIdIsProcessingRollover: activeIdIsProcessingRollover(state),
    isProcessingRollover: isProcessingRolloverSelector(state),
    hasBankAccount: isBankVerifiedSelector(state),
    iraAccountId,
    isFetching: loadingSelector(state),
    accountClosed:
      get(state.user, "userState.state", "") === "IndividualClosed",
    accountPendingClose:
      get(state.user, "userState.state", "") === "IndividualClosePending",
    transactions: state.transactions.transactions,
    userHasSetupPaycheckContribution: some(
      state.user.employerLinkRequests,
      (link) => link.status !== "PENDING" && link.status !== "REJECTED"
    ),
    hasLinkRequest: !isEmpty(state.user.employerLinkRequests),
    userInfo,
    nextPayrollDate: moment(
      get(state.user, "employerLinkRequests[0].schedule.end", null) as
        | string
        | null
    ).format("MMMM DD, YYYY"),
    userIsLocked: userIsLockedSelector(state),
    contributionAbility: contributionAbilitySelector(state),
    bankAccount: bank,
    isAccountOverview: isIndividualAccountOverview(state),
    userHasRecurringContributions: !isEmpty(
      state.contribution.recurringContributions
    ),
    error: errorSelector(state),
    activeRolloverAccount,
    accounts,
    individualReAuthRequired,
    auth0AccountSessionId: auth0AccountSessionSelector(state),
    isPaychex: userIs401kRollIn(state),
    hasPaychexRollover: userHasPaychexRollover(state),
    hasPaychexPerksLink: userHasPaychexPerksLink(state),
    hasPaychexEmployeeWorkerRecords: hasPaychexEmployeeWorkerRecords(state),
    hasPaycheckSplitContribution: !!paycheckSplitContributionSelector(state),
    isFetchingPerksContribution: perksContributionLoadingSelector(state),
    hasMixedFunds: hasBothPreTaxAndRothFundsSelector(state),
    hasPreTaxFunds: hasPreTaxFundsSelector(state),
    hasRothFunds: hasRothFundsSelector(state),
    activeAccountType: activeAccountType(state),
    location: { search: window.location.search },
  };
};
const mapDispatchToProps = {
  push,
  getAllAccounts,
  getTransactions,
  getAllDocuments,
  getLinkedEmployers,
  getEmployerLinkRequests,
  getContributionAbility,
  getUserRecurringContributions,
  getPaychexWorkerRecords,
  getPaycheckSplitContribution,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  /** invoked from _handleOnSuccess when a bank is re-authenticated */
  onSuccess?: (token: string, metadata: unknown, userLegalName: string) => void;
  /** declared in the original propTypes but never supplied by connect or read */
  activeAccountId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  initialFetching: boolean;
}

class IndividualDashboard extends React.PureComponent<Props, State> {
  _promises: unknown[] = [];

  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
    };
  }

  componentDidMount() {
    window.analytics.page("Dashboard");
    if (this.props.userIsLocked) {
      this.setState({ initialFetching: false });
      return;
    }
    const getLinkedEmployers = this.props.getLinkedEmployers(
      this.props.client as ApolloClient<object>
    );
    const linkRequestsPromise = this.props.getEmployerLinkRequests(
      this.props.client as ApolloClient<object>
    );
    this._promises.push(
      this.props.getUserRecurringContributions(
        this.props.client as ApolloClient<object>
      )
    );
    const accountPromise = this.props
      .getAllAccounts(this.props.client as ApolloClient<object>)
      .then(() => {
        if (this.props.iraAccountId) {
          void this.props.getAllDocuments(
            this.props.client as ApolloClient<object>,
            this.props.iraAccountId
          );
          const transactionPromise = this.props
            .getTransactions(
              this.props.client as ApolloClient<object>,
              this.props.iraAccountId
            )
            .then(() => {
              this.setState({ initialFetching: false });
            });
          this._promises.push(transactionPromise);
        } else {
          this.setState({ initialFetching: false });
        }
      });

    const getContributionAbility = this.props.getContributionAbility(
      this.props.client as ApolloClient<object>
    );

    // Perks funds via a PAYCHECK_SPLIT, which lives outside both
    // recurringContributions and employerLinkRequests, so it takes its own read.
    if (this.props.hasPaychexPerksLink) {
      const client = this.props.client as ApolloClient<object>;
      void this.props.getPaychexWorkerRecords(client);
      void this.props.getPaycheckSplitContribution(client);
    }

    this._promises.push(
      accountPromise,
      linkRequestsPromise,
      getLinkedEmployers,
      getContributionAbility
    );
  }

  componentWillUnmount() {
    each(this._promises as Array<{ cancel: () => void }>, (promise) => {
      promise.cancel();
    });
  }

  _handleOnSuccess = (
    token: string,
    metadata: unknown,
    userLegalName: string
  ) => {
    if (this.props.onSuccess) {
      this.props.onSuccess(token, metadata, userLegalName);
      toast.success("Bank account successfully re-authenticated.");
    }
  };

  _getPlaidLinkButton = (isExpiredBank: { id?: string } | null | undefined) => {
    return (
      <ReAuthPlaidLink bank={isExpiredBank} onSuccess={this._handleOnSuccess} />
    );
  };

  _individualReAuthCard() {
    const { bankAccount } = this.props;

    const bankAccountName = get(bankAccount, "bankName");
    const lastFourDigits = get(bankAccount, "accountId");

    const title = "Bank Authentication Required!";
    const subheader = `Re-authenticate your ${bankAccountName} bank account ending in ${lastFourDigits} to resume contributions from your linked bank account.`;

    return (
      <IconBanner
        icon={<FiAlertTriangle color="white" stroke="#B12121" size={16} />}
        color="error"
        title={title}
        subheader={subheader}
        actionComponent={this._getPlaidLinkButton(bankAccount)}
      />
    );
  }

  _needsPerksContributionSetup = () =>
    this.props.hasPaychexPerksLink &&
    this.props.hasPaychexEmployeeWorkerRecords &&
    !this.props.hasPaycheckSplitContribution &&
    !this.props.userHasRecurringContributions;

  _perksContributionCard = () => (
    <Row>
      <Col md={6}>
        <ActionCard
          onClick={() => this.props.push("/dashboard/contributions")}
          icon={<BsCashStack size={16} strokeWidth={0.5} color={"#1D99A9"} />}
          title={"Start making contributions to your IRA"}
          description={
            "Automatically set aside a portion of each paycheck for your retirement"
          }
          actionText={"Start Contributing"}
        />
      </Col>
    </Row>
  );

  _renderSuccessfulOnboardingDashboard(firstTransactionPending: boolean) {
    // Paychex Flex (Perks) users fund via a PAYCHECK_SPLIT payroll deduction,
    // not an employer link request or a bank recurring contribution, so they
    // match neither userHasSetupPaycheckContribution nor
    // userHasRecurringContributions. Treat them as a payroll contributor here so
    // they get contribution status text instead of a bare "Great News".
    const employeePayrollContributionProcessing =
      (this.props.userHasSetupPaycheckContribution ||
        this.props.hasPaychexPerksLink) &&
      firstTransactionPending;
    // contribution might not be processed yet by internal service due to async contribution processing
    const selfEmployedContributionProcessing =
      this.props.userHasRecurringContributions && firstTransactionPending;

    const shouldShowContributionProcessingText =
      employeePayrollContributionProcessing ||
      selfEmployedContributionProcessing;

    const userHasAProcessingRollover = this.props.activeRolloverAccount;

    const title = `Great News, ${String(this.props.userInfo.firstName)}!`;

    // PaychexRolloverStatus already covers these for Paychex roll-in holders on
    // the hybrid dashboard; rendering both would double the cards up.
    const showGenericRolloverCards = !this.props.hasPaychexRollover;

    let messageContent: React.ReactNode;

    if (shouldShowContributionProcessingText) {
      messageContent = (
        <p className="email-details">
          Your account is now open and your recurring contribution has been set
          up! Once your contribution processes and is fully posted, you&apos;ll
          be able to see your account balance and latest transactions.
        </p>
      );
    } else if (this.props.userHasRecurringContributions) {
      messageContent = (
        <p className="email-details">
          We have setup your recurring contribution. Once your contribution
          processes and has fully posted you will be able to see your account
          balance and latest transactions.
        </p>
      );
    } else if (
      this.props.userHasSetupPaycheckContribution ||
      this.props.hasPaychexPerksLink
    ) {
      messageContent = (
        <p className="email-details">
          We have setup your payroll contribution amount. Once your employer
          runs payroll and your contribution has fully posted you will then be
          able to see your account balance and latest transactions.
        </p>
      );
    } else if (userHasAProcessingRollover) {
      messageContent = (
        <p className="email-details">
          Your account is now open! Once you complete the rollover or make an
          initial deposit you will see your funds in your account.
        </p>
      );
    }

    return (
      <div className="mega-container">
        <section className="page-title-wrap">
          <article className="text-cell">
            <div className="main-content">
              {this.props.individualReAuthRequired &&
                this._individualReAuthCard()}
              {this._shouldShowPriorYearContributionCard() && (
                <Row>
                  <Col md={12}>
                    <PriorYearContributionCard
                      priorYearContributionAbility={get(
                        this.props,
                        "contributionAbility.priorYear"
                      )}
                      hasBankAccount={this.props.hasBankAccount}
                      bankAccount={this.props.bankAccount}
                    />
                  </Col>
                </Row>
              )}
              {showGenericRolloverCards &&
                this.props.activeIdIsProcessingRollover && (
                  <IndividualRolloverCard
                    title="You have an active rollover in progress"
                    subheader="For instructions, please refer to the information provided here."
                  />
                )}
              {showGenericRolloverCards &&
                !this.props.activeIdIsProcessingRollover &&
                this.props.rolloverAccountIsActive && (
                  <IndividualRolloverFormCard
                    auth0AccountSessionId={this.props.auth0AccountSessionId}
                  />
                )}

              {this._needsPerksContributionSetup() ? (
                this._perksContributionCard()
              ) : (
                <Card>
                  <Container fluid="md">
                    <Row>
                      <Col>
                        <IconHeader variant="pageHeader" headerText={title} />
                        <IconSubheader subheader={messageContent} />
                      </Col>
                    </Row>
                  </Container>
                </Card>
              )}
            </div>
          </article>
        </section>
      </div>
    );
  }

  _renderMultiAccountOverviewDashboard() {
    const regularRolloverIsProcessing =
      !this.props.isPaychex && this.props.isProcessingRollover;

    const isPendingRolloverForm =
      !this.props.activeIdIsProcessingRollover &&
      this.props.rolloverAccountIsActive;

    const regularRolloverIsPendingRolloverForm =
      !this.props.isPaychex && isPendingRolloverForm;

    const isDualRollover = this.props.isRolloverIsMixed;

    const title = isDualRollover
      ? "You have active rollovers in progress."
      : "You have an active rollover in progress.";

    const subheader =
      "For instructions, please refer to the information provided here.";

    return (
      <>
        {this.props.individualReAuthRequired && this._individualReAuthCard()}
        {regularRolloverIsProcessing && (
          <IndividualRolloverCard title={title} subheader={subheader} />
        )}
        {regularRolloverIsPendingRolloverForm && (
          <IndividualRolloverFormCard
            auth0AccountSessionId={this.props.auth0AccountSessionId}
          />
        )}
        <IndividualOverview />
      </>
    );
  }

  _renderAccountClosedDashboard() {
    return (
      <>
        <Card className="account-closed-card">
          <IconMessageCard
            header="Account Closed"
            subheader="Your account is closed. This process can take up to 15 business days
            for all funds to settle and be disbursed to you. We will contact you
            directly for any additional steps you may need to complete. If you
            have any questions, let us know at the support link below."
            color="error"
            icon={<FiAlertTriangle color="white" stroke="#B12121" size={16} />}
            actionComponent={<SupportEmailLink />}
          />
        </Card>
        <ContributionHistory />
      </>
    );
  }

  _renderFullDashboard() {
    // See _renderSuccessfulOnboardingDashboard — PaychexRolloverStatus owns these
    // for Paychex roll-in holders.
    const showGenericRolloverCards = !this.props.hasPaychexRollover;

    return (
      <div className="mega-container">
        <section className="page-title-wrap">
          <article className="text-cell">
            <div className="main-content">
              {this.props.individualReAuthRequired &&
                this._individualReAuthCard()}

              {this._shouldShowPriorYearContributionCard() && (
                <Row>
                  <Col md={12}>
                    <PriorYearContributionCard
                      priorYearContributionAbility={get(
                        this.props,
                        "contributionAbility.priorYear"
                      )}
                      hasBankAccount={this.props.hasBankAccount}
                      bankAccount={this.props.bankAccount}
                    />
                  </Col>
                </Row>
              )}

              {showGenericRolloverCards &&
                this.props.activeIdIsProcessingRollover && (
                  <IndividualRolloverCard
                    title="You have an active rollover in progress"
                    // PRESERVED BUG (conversion-only): the prop is `subtext`, but
                    // IndividualRolloverCard only reads `subheader` — the subtext
                    // here is silently ignored. Kept verbatim.
                    // @ts-expect-error subtext is not a valid IndividualRolloverCard prop
                    subtext="For instructions, please refer to the information provided here."
                  />
                )}
              {showGenericRolloverCards &&
                !this.props.activeIdIsProcessingRollover &&
                this.props.rolloverAccountIsActive && (
                  <IndividualRolloverFormCard
                    auth0AccountSessionId={this.props.auth0AccountSessionId}
                  />
                )}

              {this._needsPerksContributionSetup() &&
                this._perksContributionCard()}

              <Row>
                <Col md={12}>
                  <BalanceKpi hasBankAccount={this.props.hasBankAccount} />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <PerformanceTable />
                </Col>
              </Row>
            </div>
          </article>
        </section>
      </div>
    );
  }

  _renderAccountLockedDashboard() {
    return (
      <Card className="account-closed-card">
        <Card>
          <IconMessageCard
            header="Account Locked"
            subheader="Your account is locked. If you have any questions, let us know at the support link below."
            color="error"
            icon={<FiAlertTriangle color="white" stroke="#B12121" size={16} />}
            actionComponent={<SupportEmailLink />}
          />
        </Card>
      </Card>
    );
  }

  _shouldShowPriorYearContributionCard = () => {
    const isWithinPriorYearContributionWindow = get(
      this.props,
      "contributionAbility.isWithinPriorYearContributionWindow",
      false
    );
    const priorYearContributionAbility = get(
      this.props,
      "contributionAbility.priorYear"
    );
    const canContributeToPriorYear = get(
      priorYearContributionAbility,
      "canContribute"
    );
    return isWithinPriorYearContributionWindow && canContributeToPriorYear;
  };

  _renderNormalDashboard = () => {
    let content;

    const noTransactions = isEmpty(this.props.transactions);

    let firstTransactionPending = false;

    if (!noTransactions) {
      const firstTransaction = this.props.transactions[0];
      firstTransactionPending =
        size(this.props.transactions) === 1 &&
        firstTransaction.status !== "COMPLETE";
    }

    if (this.props.isAccountOverview) {
      content = this._renderMultiAccountOverviewDashboard();
    } else if (!this.props.hasFundedAccount || noTransactions) {
      content = this._renderSuccessfulOnboardingDashboard(
        firstTransactionPending
      );
    } else {
      content = this._renderFullDashboard();
    }
    return <>{content}</>;
  };

  render() {
    let content;

    if (this.state.initialFetching || this.props.isFetchingPerksContribution) {
      content = null;
    } else if (this.props.userIsLocked) {
      content = this._renderAccountLockedDashboard();
    } else if (this.props.accountPendingClose) {
      content = <PendingClosure />;
    } else if (this.props.accountClosed) {
      content = this._renderAccountClosedDashboard();
    } else if (
      this.props.hasPaychexRollover &&
      !this.props.hasPaychexPerksLink
    ) {
      content = <PaychexDashboard />;
    } else if (
      this.props.hasPaychexRollover &&
      this.props.hasPaychexPerksLink
    ) {
      // Holds both Paychex products — keep the 401k roll-in steps above the
      // standard dashboard instead of letting one product's UI replace the other's.
      content = (
        <>
          <PaychexRolloverStatus />
          <PaychexFirstRolloverCard />
          <PaychexSecondRolloverCard />
          {this._renderNormalDashboard()}
        </>
      );
    } else {
      content = this._renderNormalDashboard();
    }

    const isFetching =
      this.props.isFetching ||
      this.state.initialFetching ||
      this.props.isFetchingPerksContribution;
    const { noAccount: noAccountParam } = queryString.parse(
      get(this.props, "location.search", "")
    );
    return (
      <div className="dashboard-content">
        {isFetching && !this.props.userIsLocked && <IconSpinner centered />}
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        {noAccountParam === "true" && (
          <Alert
            type="warning"
            msg="Select an account from the dropdown at the top of the page before navigating away from the dashboard."
          />
        )}
        {content}
      </div>
    );
  }
}

export default connector(withApollo<Props>(IndividualDashboard));
